/**
 * PostHog analytics wrapper — graceful no-op when key is missing.
 *
 * Server-side events (emitted by the FastAPI backend) are the trustworthy
 * funnel signal; these client-side calls are UI-level complements that can
 * be blocked by adblockers.
 *
 * NEVER pass PII here. No email, name, proposal text, NOFO text, chatbot
 * content. Use enums, counts, and category strings.
 */

import posthog from 'posthog-js';

const API_KEY = process.env.REACT_APP_POSTHOG_KEY;
const API_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

export function initPostHog() {
  if (initialized || !API_KEY) {
    if (!API_KEY) {
      console.info('PostHog disabled: REACT_APP_POSTHOG_KEY not set');
    }
    return;
  }
  try {
    posthog.init(API_KEY, {
      api_host: API_HOST,
      autocapture: true,
      capture_pageview: false, // custom view-based routing — we fire manually
      capture_pageleave: true,
      person_profiles: 'always',
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '.ph-mask',
        recordCrossOriginIframes: false,
      },
      // Honor Do Not Track header — respectful default
      respect_dnt: true,
      loaded: (ph) => {
        if (process.env.NODE_ENV === 'development') {
          ph.debug(false);
        }
      },
    });
    initialized = true;
  } catch (err) {
    console.debug('PostHog init failed:', err);
  }
}

export function isPostHogEnabled() {
  return initialized;
}

export function track(event, properties) {
  if (!initialized) return;
  try {
    posthog.capture(event, properties || {});
  } catch (err) {
    console.debug('PostHog capture failed:', err);
  }
}

export function identify(distinctId, properties, setOnce) {
  if (!initialized || !distinctId) return;
  try {
    const props = { ...(properties || {}) };
    if (setOnce) props.$set_once = setOnce;
    posthog.identify(distinctId, props);
  } catch (err) {
    console.debug('PostHog identify failed:', err);
  }
}

export function alias(distinctId) {
  if (!initialized || !distinctId) return;
  try {
    posthog.alias(distinctId);
  } catch (err) {
    console.debug('PostHog alias failed:', err);
  }
}

export function reset() {
  if (!initialized) return;
  try {
    posthog.reset();
  } catch (err) {
    console.debug('PostHog reset failed:', err);
  }
}

export function pageview(view, properties) {
  if (!initialized) return;
  try {
    posthog.capture('$pageview', { view, ...(properties || {}) });
  } catch (err) {
    console.debug('PostHog pageview failed:', err);
  }
}

const analytics = {
  initPostHog,
  isPostHogEnabled,
  track,
  identify,
  alias,
  reset,
  pageview,
};

export default analytics;
